/**
 * InvoiceDetailPage — a single invoice: header, line items, and totals.
 *
 * Phase 12 / Task 12.3. Reads GET /v1/billing/invoices/:id. This is an
 * INVOICE-FACING surface, so it renders the legal brand "Paelode Systems"
 * (BRAND.legal) — the product chrome (AppShell wordmark) stays "lodeX Pro"
 * (CLAUDE.md branding split). Money via formatUsdFromCents; dates via the local
 * timestamp formatter (DEC-004).
 *
 * Carries the invoice lifecycle actions (86bc83kk9): Send, Mark paid and Void. Before
 * those existed every invoice stayed DRAFT forever, so /billing/aging — which reads
 * status = 'SENT' — could never show a row in real use. The server owns the legal
 * transition graph; the buttons here only offer what is legal from the current status,
 * which is UX, not a trust boundary.
 */

import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { BRAND } from '@lodex-pro/shared/brand';
import { Button } from 'src/components/ui/button';
import { StatusLabel } from 'src/components/ui/status-label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';

import { formatUsdFromCents } from '../lib/money';
import {
  formatCalendarDate,
  formatCreated,
  formatDate,
  formatPaymentTerms,
} from '../lib/timestamp';
import { useUserTimezone } from '../lib/use-user-timezone';
import { DownloadInvoicePdf } from '../features/billing/DownloadInvoicePdf';
import { invoiceStatusTone } from '../features/billing/status-tone';
import { toast } from 'sonner';
import {
  useInvoiceDetail,
  useInvoiceTransition,
  type InvoiceDetailHeader,
  type InvoiceTransitionResponse,
} from '../features/billing/hooks';

/** Gap 6 (0250): a service date (a calendar day, no zone), or a dash before 0250. */
function serviceDay(d: string | null | undefined): string {
  return d == null ? '—' : formatCalendarDate(d);
}

/** Small labelled key/value used in the invoice header grid. */
function Field({ label, value }: { label: string; value: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>{label}</span>
      <span style={{ fontSize: '14px', color: 'var(--color-text-primary)' }}>{value}</span>
    </div>
  );
}

export function InvoiceDetailPage(): React.JSX.Element {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const tz = useUserTimezone();
  const id = params.id ?? null;
  const { data, isLoading, isError, refetch } = useInvoiceDetail(id);

  return (
    <div style={{ padding: '24px', maxWidth: '960px' }}>
      <Button
        variant="ghost"
        onClick={(): void => {
          void navigate('/invoices');
        }}
        style={{ marginBottom: '16px' }}
      >
        <ArrowLeft size={16} aria-hidden="true" style={{ marginRight: '6px' }} />
        Back to invoices
      </Button>

      {isLoading && (
        <p role="status" style={{ color: 'var(--color-text-secondary)' }}>
          Loading invoice…
        </p>
      )}

      {isError && (
        <div style={{ color: 'var(--color-danger)' }}>
          <p style={{ marginBottom: '8px' }}>Couldn&apos;t load this invoice.</p>
          <Button
            variant="outline"
            onClick={(): void => {
              void refetch();
            }}
          >
            Retry
          </Button>
        </div>
      )}

      {!isLoading && !isError && data !== undefined && (
        <div
          style={{
            borderRadius: 'var(--radius-card)',
            backgroundColor: 'var(--color-bg-secondary)',
            padding: '24px',
          }}
        >
          {/* Invoice-facing masthead — legal brand "Paelode Systems". */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: '12px',
              borderBottom: '1px solid var(--color-border-primary)',
              paddingBottom: '16px',
              marginBottom: '20px',
            }}
          >
            <div>
              <div style={{ fontSize: '20px', fontWeight: 600 }}>{BRAND.legal}</div>
              <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>Invoice</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '18px', fontWeight: 600 }}>{data.invoice.invoiceNumber}</div>
              <StatusLabel tone={invoiceStatusTone(data.invoice.status)}>
                {data.invoice.status}
              </StatusLabel>
              <InvoiceActions id={id} status={data.invoice.status} timeZone={tz} />
              {/* #50 PR D — summary + one backup page per ticket. */}
              {id !== null && (
                <div style={{ marginTop: '8px' }}>
                  <DownloadInvoicePdf id={id} />
                </div>
              )}
            </div>
          </div>

          <LifecycleRecord invoice={data.invoice} timeZone={tz} />

          {/* Header fields */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '16px',
              marginBottom: '24px',
            }}
          >
            <Field label="Operator" value={data.invoice.operatorName ?? '—'} />
            <Field
              label="Billing period"
              value={`${formatDate(data.invoice.periodStart, tz)} – ${formatDate(
                data.invoice.periodEnd,
                tz,
              )}`}
            />
            <Field label="Generated" value={formatCreated(data.invoice.generatedAt, tz)} />
            <Field label="Tickets covered" value={data.jobs.length} />
            {/* DEC-326: fixed when the invoice is sent. A draft has no terms yet. */}
            <Field
              label="Terms"
              value={
                data.invoice.paymentTermsDays === null
                  ? 'Set when sent'
                  : formatPaymentTerms(data.invoice.paymentTermsDays)
              }
            />
            <Field
              label="Due date"
              value={data.invoice.dueDate === null ? '—' : formatCalendarDate(data.invoice.dueDate)}
            />
          </div>

          {/* Line items */}
          <div
            style={{
              borderRadius: 'var(--radius-card)',
              backgroundColor: 'var(--color-bg-secondary)',
              overflow: 'hidden',
              marginBottom: '20px',
            }}
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.lineItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} style={{ color: 'var(--color-text-tertiary)' }}>
                      No line items.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.lineItems.map((li, idx) => (
                    <TableRow key={`${li.jobId ?? 'none'}-${li.sortOrder}-${idx}`}>
                      <TableCell>{serviceDay(li.serviceDate)}</TableCell>
                      <TableCell>{li.lineType}</TableCell>
                      <TableCell className="text-right">{li.quantity}</TableCell>
                      <TableCell>{li.unitLabel ?? '—'}</TableCell>
                      <TableCell className="text-right">
                        {formatUsdFromCents(li.rateCents)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatUsdFromCents(li.amountCents)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Totals */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div
              style={{ minWidth: '260px', display: 'flex', flexDirection: 'column', gap: '8px' }}
            >
              <TotalRow label="Subtotal" value={formatUsdFromCents(data.invoice.subtotalCents)} />
              <TotalRow label="Tax" value={formatUsdFromCents(data.invoice.taxCents)} />
              <div
                style={{
                  borderTop: '1px solid var(--color-border-primary)',
                  paddingTop: '8px',
                }}
              >
                <TotalRow
                  label="Total"
                  value={formatUsdFromCents(data.invoice.totalCents)}
                  emphasize
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** The user's local calendar day as 'YYYY-MM-DD' (DEC-004 / DEC-066) — in THEIR zone,
 *  the same day the API checks a paid date against, never the browser's or UTC's. */
export function localTodayIso(timeZone: string, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const get = (t: string): string => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

// DEC-322 and DEC-326 share one calendar-date formatter (lib/timestamp). Re-exported
// here because this page's tests import it from the page.
export { formatCalendarDate };

const MIN_VOID_REASON = 5;

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  color: 'var(--color-text-secondary)',
  marginBottom: '4px',
  textAlign: 'left',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '38px',
  padding: '8px 10px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
  boxSizing: 'border-box',
};

const panelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  width: '320px',
  maxWidth: '100%',
  marginLeft: 'auto',
  padding: '12px',
  borderRadius: '12px',
  backgroundColor: 'var(--color-bg-tertiary)',
};

/**
 * The lifecycle actions legal from `status`. Nothing is rendered for PAID or VOID —
 * both are terminal, and offering a dead button is worse than offering none.
 *
 * DEC-322: Mark as paid opens a small form for the payment reference and the date the
 * payment arrived (defaulting to the user's local today, never later). Void opens a form
 * that requires a reason and says what voiding now does: the invoice's tickets go back to
 * the Pipeline to be billed again. Both are inline rather than window.confirm, because
 * each needs input and void cannot be undone by another action.
 */
function InvoiceActions({
  id,
  status,
  timeZone,
}: {
  id: string | null;
  status: string;
  timeZone: string;
}): React.JSX.Element | null {
  const [panel, setPanel] = React.useState<'none' | 'pay' | 'void'>('none');
  const [reason, setReason] = React.useState('');
  const [reference, setReference] = React.useState('');
  const today = localTodayIso(timeZone);
  const [paidDate, setPaidDate] = React.useState(today);

  const onDone = (result: InvoiceTransitionResponse): void => {
    setPanel('none');
    if (result.released !== undefined) {
      const n = result.released.ticketCount;
      toast.success(
        n === 0
          ? 'Invoice voided.'
          : `Invoice voided. ${String(n)} ${n === 1 ? 'ticket is' : 'tickets are'} back in the Pipeline to bill again.`,
      );
    }
  };

  const send = useInvoiceTransition(id, 'send', onDone);
  const markPaid = useInvoiceTransition(id, 'mark-paid', onDone);
  const voidInv = useInvoiceTransition(id, 'void', onDone);

  const upper = status.toUpperCase();
  const canSend = upper === 'DRAFT';
  const canPay = upper === 'SENT';
  const canVoid = upper === 'DRAFT' || upper === 'SENT';
  if (!canSend && !canPay && !canVoid) return null;

  const busy = send.isPending || markPaid.isPending || voidInv.isPending;
  const reasonOk = reason.trim().length >= MIN_VOID_REASON;
  // 'YYYY-MM-DD' strings compare correctly as text.
  const dateOk = /^\d{4}-\d{2}-\d{2}$/.test(paidDate) && paidDate <= today;
  const payOk = reference.trim().length > 0 && dateOk;

  return (
    <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {panel === 'none' && (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          {canSend && (
            <Button
              size="sm"
              disabled={busy}
              onClick={(): void => {
                // Body is {} and never undefined — see InvoiceTransitionBodies.
                send.mutate({});
              }}
            >
              {send.isPending ? 'Sending…' : 'Mark as sent'}
            </Button>
          )}
          {canPay && (
            <Button
              size="sm"
              disabled={busy}
              onClick={(): void => {
                setPaidDate(today);
                setPanel('pay');
              }}
            >
              Mark as paid
            </Button>
          )}
          {canVoid && (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={(): void => {
                setPanel('void');
              }}
            >
              Void
            </Button>
          )}
        </div>
      )}

      {panel === 'pay' && (
        <div style={panelStyle}>
          <div>
            <label style={labelStyle} htmlFor="pay-ref">
              Payment reference (cheque or wire number)
            </label>
            <input
              className="keep-case"
              id="pay-ref"
              type="text"
              maxLength={100}
              value={reference}
              onChange={(e): void => setReference(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle} htmlFor="pay-date">
              Date paid
            </label>
            <input
              id="pay-date"
              type="date"
              max={today}
              value={paidDate}
              onChange={(e): void => setPaidDate(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={(): void => setPanel('none')}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={busy || !payOk}
              onClick={(): void => {
                markPaid.mutate({ paymentReference: reference.trim(), paidDate });
              }}
            >
              {markPaid.isPending ? 'Saving…' : 'Save payment'}
            </Button>
          </div>
        </div>
      )}

      {panel === 'void' && (
        <div style={panelStyle}>
          <p style={{ margin: 0, fontSize: '13px', textAlign: 'left' }}>
            Voiding keeps this invoice as a record and sends its tickets back to the Pipeline, so
            you can bill them again on a new invoice.
          </p>
          <div>
            <label style={labelStyle} htmlFor="void-reason">
              Reason for voiding
            </label>
            <textarea
              id="void-reason"
              rows={3}
              maxLength={500}
              value={reason}
              onChange={(e): void => setReason(e.target.value)}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={(): void => setPanel('none')}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={busy || !reasonOk}
              onClick={(): void => {
                voidInv.mutate({ reason: reason.trim() });
              }}
            >
              {voidInv.isPending ? 'Voiding…' : 'Void invoice'}
            </Button>
          </div>
        </div>
      )}

      {/* Derived from mutation state -- never setState during render. */}
      {(send.isError || markPaid.isError || voidInv.isError) && (
        <span role="alert" style={{ fontSize: '12px', color: 'var(--color-danger)' }}>
          That action was refused. Reload the invoice and check its current status.
        </span>
      )}
    </div>
  );
}

/**
 * What happened to a closed invoice (DEC-322): when and how it was paid, or who voided
 * it and why. Invoices voided before reasons were required say so rather than showing
 * a blank.
 */
function LifecycleRecord({
  invoice,
  timeZone,
}: {
  invoice: InvoiceDetailHeader;
  timeZone: string;
}): React.JSX.Element | null {
  const upper = invoice.status.toUpperCase();
  if (upper === 'PAID') {
    return (
      <div role="status" style={{ fontSize: '13px', marginBottom: '20px' }}>
        Paid
        {invoice.paymentDate !== null ? ` ${formatCalendarDate(invoice.paymentDate)}` : ''}
        {invoice.paymentReference !== null ? ` · Ref ${invoice.paymentReference}` : ''}
      </div>
    );
  }
  if (upper === 'VOID') {
    return (
      <div
        role="status"
        style={{
          fontSize: '13px',
          marginBottom: '20px',
          padding: '10px 12px',
          borderRadius: '12px',
          backgroundColor: 'var(--color-bg-tertiary)',
        }}
      >
        <div>
          Voided
          {invoice.voidAt !== null ? ` ${formatCreated(invoice.voidAt, timeZone)}` : ''}
          {invoice.voidedByName !== null ? ` by ${invoice.voidedByName}` : ''}.
        </div>
        <div style={{ color: 'var(--color-text-secondary)', marginTop: '4px' }}>
          {invoice.voidReason !== null
            ? `Reason: ${invoice.voidReason}`
            : 'No reason recorded (voided before reasons were required).'}
        </div>
      </div>
    );
  }
  return null;
}

function TotalRow({
  label,
  value,
  emphasize = false,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
}): React.JSX.Element {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <span
        style={{
          fontSize: emphasize ? '15px' : '14px',
          fontWeight: emphasize ? 600 : 400,
          color: emphasize ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: emphasize ? '18px' : '14px',
          fontWeight: emphasize ? 600 : 400,
          color: 'var(--color-text-primary)',
        }}
      >
        {value}
      </span>
    </div>
  );
}
